import moment from 'moment';
import {
    LeoSubscriptionAttentionItem,
    LeoSubscriptionFlattenedRow,
    LeoSubscriptionScenario,
    LeoSubscriptionUser,
} from '../../../types/subscriptions';

const formatWords = (value?: string | null) => {
    if (!value) return 'Not set';

    return value
        .split('_')
        .join(' ')
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const formatFractionDigits = (amount: number) => {
    return Number.isInteger(amount) ? 0 : 2;
};

const getScenarioRow = (
    user: LeoSubscriptionUser,
    propertyName: string,
    clientName: string | null,
    scenario: LeoSubscriptionScenario,
) => {
    return {
        id: `${user.userId}-${propertyName}-${scenario.scenarioName}-scenario`,
        scope: 'Scenario' as const,
        userId: user.userId,
        userName: user.userName,
        userEmail: user.userEmail,
        propertyName,
        clientName,
        scenarioName: scenario.scenarioName,
        subscriptionRenewalDate: scenario.subscriptionRenewalDate,
        daysUntilRenewal: scenario.daysUntilRenewal,
        highlightRenewal: scenario.highlightRenewal,
        conditionalFormatting: scenario.conditionalFormatting,
        subscriptionId: scenario.subscriptionId,
        subscriptionStatus: scenario.subscriptionStatus,
        interval: scenario.interval,
        amount: scenario.amount,
        currency: scenario.currency,
        planName: scenario.planName,
    };
};

export const flattenSubscriptionRows = (
    items: LeoSubscriptionUser[] = [],
): LeoSubscriptionFlattenedRow[] => {
    return items.flatMap((user) =>
        (user.properties || []).flatMap((property) => {
            const propertyRow: LeoSubscriptionFlattenedRow = {
                id: `${user.userId}-${property.propertyName}-property`,
                scope: 'Property',
                userId: user.userId,
                userName: user.userName,
                userEmail: user.userEmail,
                propertyName: property.propertyName,
                clientName: property.clientName,
                scenarioName: null,
                subscriptionRenewalDate: property.subscriptionRenewalDate,
                daysUntilRenewal: property.daysUntilRenewal,
                highlightRenewal: property.highlightRenewal,
                conditionalFormatting: property.conditionalFormatting,
                subscriptionId: property.subscriptionId,
                subscriptionStatus: property.subscriptionStatus,
                interval: property.interval,
                amount: property.amount,
                currency: property.currency,
                planName: property.planName,
            };

            const scenarioRows = (property.scenarios || []).map((scenario) =>
                getScenarioRow(
                    user,
                    property.propertyName,
                    property.clientName,
                    scenario,
                ),
            );

            return [propertyRow, ...scenarioRows];
        }),
    );
};

export const collectAttentionItems = (
    items: LeoSubscriptionUser[] = [],
): LeoSubscriptionAttentionItem[] => {
    const attentionItems = items.flatMap((user) =>
        (user.properties || []).flatMap<LeoSubscriptionAttentionItem>(
            (property) => {
            if (property.highlightRenewal) {
                return [
                    {
                        id: `${user.userId}-${property.propertyName}-property`,
                        scope: 'Property' as const,
                        userName: user.userName,
                        userEmail: user.userEmail,
                        propertyName: property.propertyName,
                        scenarioName: null,
                        subscriptionRenewalDate:
                            property.subscriptionRenewalDate,
                        daysUntilRenewal: property.daysUntilRenewal,
                        reason:
                            property.conditionalFormatting?.reason || null,
                        planName: property.planName,
                        subscriptionStatus: property.subscriptionStatus,
                        amount: property.amount,
                        currency: property.currency,
                    },
                ];
            }

            return (property.scenarios || [])
                .filter((scenario) => scenario.highlightRenewal)
                .map((scenario) => ({
                    id: `${user.userId}-${property.propertyName}-${scenario.scenarioName}-scenario`,
                    scope: 'Scenario' as const,
                    userName: user.userName,
                    userEmail: user.userEmail,
                    propertyName: property.propertyName,
                    scenarioName: scenario.scenarioName,
                    subscriptionRenewalDate: scenario.subscriptionRenewalDate,
                    daysUntilRenewal: scenario.daysUntilRenewal,
                    reason: scenario.conditionalFormatting?.reason || null,
                    planName: scenario.planName,
                    subscriptionStatus: scenario.subscriptionStatus,
                    amount: scenario.amount,
                    currency: scenario.currency,
                }));
            },
        ),
    );

    return attentionItems.sort((first, second) => {
        const firstValue = first.daysUntilRenewal ?? Number.MAX_SAFE_INTEGER;
        const secondValue = second.daysUntilRenewal ?? Number.MAX_SAFE_INTEGER;
        return firstValue - secondValue;
    });
};

export const formatSubscriptionDate = (value?: string | null) => {
    if (!value) return 'Not set';
    return moment(value).format('DD MMM YYYY');
};

export const formatSubscriptionStatus = (value?: string | null) => {
    return formatWords(value);
};

export const formatSubscriptionInterval = (value?: string | null) => {
    return formatWords(value);
};

export const formatSubscriptionAmount = (
    amount?: number | null,
    currency?: string | null,
) => {
    if (amount == null || Number.isNaN(Number(amount))) return 'Not set';

    const parsedAmount = Number(amount);
    const parsedCurrency = (currency || 'GBP').toUpperCase();

    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: parsedCurrency,
            minimumFractionDigits: formatFractionDigits(parsedAmount),
            maximumFractionDigits: formatFractionDigits(parsedAmount),
        }).format(parsedAmount);
    } catch (error) {
        const fractionDigits = formatFractionDigits(parsedAmount);
        return `${parsedAmount.toFixed(fractionDigits)} ${parsedCurrency}`;
    }
};
